import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import AdForgeLogo from '../../components/feature/AdForgeLogo';

export default function LoginPage() {
  const { signIn, signInWithGoogle } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Please fill in all fields.');
      return;
    }
    setError('');
    setIsLoading(true);
    const { error: authError } = await signIn(email, password);
    setIsLoading(false);
    if (authError) {
      setError(authError);
    } else {
      // Check if email is verified
      const { data: { user } } = await supabase.auth.getUser();
      if (user && !user.email_confirmed_at) {
        setError('Please verify your email before signing in. Check your inbox for the confirmation link.');
        await supabase.auth.signOut();
        return;
      }
      navigate('/dashboard');
    }
  };

  const handleGoogle = async () => {
    setError('');
    setIsGoogleLoading(true);
    const { error: authError } = await signInWithGoogle();
    setIsGoogleLoading(false);
    if (authError) setError(authError);
    // on success, Supabase redirects the browser automatically
  };

  return (
    <div className="min-h-screen bg-stone-50 font-sans flex">
      <div className="hidden lg:flex lg:w-1/2 bg-stone-950 flex-col justify-between p-12">
        <Link to="/">
          <AdForgeLogo size="md" light={true} />
        </Link>
        <div>
          <blockquote className="text-xl font-semibold text-white leading-relaxed mb-6">
            "AdForge saved me 6 hours a week and helped me land 3 new clients in my first month. The copy is genuinely better than what I was writing myself."
          </blockquote>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-gradient-to-br from-orange-500 to-amber-400 text-white font-bold text-sm">P</div>
            <div>
              <div className="text-sm font-semibold text-white">Priya Nair</div>
              <div className="text-xs text-stone-400">Founder, Bloom Social Media</div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex gap-1">
            {Array.from({ length: 5 }).map((_, i) => (
              <i key={i} className="ri-star-fill text-amber-400 text-sm" />
            ))}
          </div>
          <span className="text-sm text-stone-400">Rated 4.9/5 by 3,200+ businesses</span>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8">
            <Link to="/">
              <AdForgeLogo size="lg" light={false} />
            </Link>
          </div>

          <h1 className="text-2xl font-extrabold text-stone-900 mb-1">Welcome back</h1>
          <p className="text-sm text-stone-500 mb-8">
            Don&apos;t have an account?{' '}
            <Link to="/signup" className="text-orange-500 font-semibold hover:underline">Sign up free</Link>
          </p>

          {error && (
            <div className="mb-4 px-4 py-3 rounded-xl bg-rose-50 border border-rose-100 text-rose-600 text-sm">
              {error}
            </div>
          )}

          <button
            type="button"
            onClick={handleGoogle}
            disabled={isGoogleLoading || isLoading}
            className="w-full flex items-center justify-center gap-3 py-3.5 rounded-xl border border-stone-200 bg-white text-stone-700 font-semibold text-sm hover:bg-stone-50 hover:border-stone-300 transition-all duration-200 whitespace-nowrap cursor-pointer disabled:opacity-70 mb-5"
          >
            {isGoogleLoading ? (
              <i className="ri-loader-4-line animate-spin text-base" />
            ) : (
              <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
                <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
                <path d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z" fill="#FBBC05"/>
                <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
              </svg>
            )}
            Continue with Google
          </button>

          <div className="relative mb-5">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-stone-200" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-stone-50 px-3 text-xs text-stone-400">or sign in with email</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div>
              <label className="block text-xs font-semibold text-stone-700 mb-1.5">Email address</label>
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full text-sm px-4 py-3 rounded-xl border border-stone-200 bg-stone-50 text-stone-800 placeholder:text-stone-400 focus:outline-none focus:border-orange-400 focus:bg-white transition-all duration-200"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-xs font-semibold text-stone-700">Password</label>
                <Link to="/forgot-password" className="text-xs text-orange-500 font-semibold hover:underline">
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <input
                  type={showPass ? 'text' : 'password'}
                  name="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full text-sm px-4 py-3 pr-11 rounded-xl border border-stone-200 bg-stone-50 text-stone-800 placeholder:text-stone-400 focus:outline-none focus:border-orange-400 focus:bg-white transition-all duration-200"
                />
                <button
                  type="button"
                  onClick={() => setShowPass(!showPass)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-stone-400 hover:text-stone-600 cursor-pointer"
                >
                  <i className={`ri-${showPass ? 'eye-off' : 'eye'}-line text-sm`} />
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-gradient-to-r from-orange-500 to-amber-400 text-white font-bold text-sm hover:from-orange-600 hover:to-amber-500 transition-all duration-200 whitespace-nowrap cursor-pointer disabled:opacity-70"
            >
              {isLoading ? <><i className="ri-loader-4-line animate-spin" /> Signing in...</> : 'Sign In'}
            </button>
          </form>

          <p className="mt-6 text-xs text-stone-400 text-center">
            By signing in, you agree to our{' '}
            <a href="#" className="text-orange-500 hover:underline">Terms</a> and{' '}
            <a href="#" className="text-orange-500 hover:underline">Privacy Policy</a>.
          </p>
        </div>
      </div>
    </div>
  );
}
